import type { BrowserBroadcast } from '@adapters/broadcast/browserBroadcast';
import { ProcessPage, ProcessPageNavigation } from '@adapters/navigation/page';
import { Component, Inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import type { Logger } from '@core/model/domain/logger';
import {
  browserBroadcastToken,
  isSignalREnabledToken,
  isTrackingEnabledToken,
  loggerToken,
} from '@di/angularProviderMapper';
import { TranslateService } from '@ngx-translate/core';
import {
  AbandonEvent,
  AcceptedProcessEvent,
  AccountsFoundEvent,
  BankChoiceEvent,
  ConsentEvent,
  ContinueAfterErrorEvent,
  ContinueEvent,
  GoBackToBankChoiceEvent,
  InternalErrorEvent,
  LoginWebauthWithForm,
  ParentProcessEvent,
  ProcessEventFactory,
  ProcessEventType,
  ScaEvent,
  ScaFailedEvent,
  ScaGoBackEvent,
  SelectAccountsEvent,
  TrialsExhaustedEvent,
} from '@shared/models/processEvents';
import { CaseService } from '@shared/services/case.service';
import { ProcessEventService } from '@shared/services/process-event.service';
import { StoreService } from '@shared/services/store.service';
import { Option } from '@core/model/framework/Result/Option';
import { InitVM } from '@modules/init/init.viewmodel';
import { TrackerService } from '@shared/services/tracker.service';
import { SignalR } from '@core/model/framework/notifications/signalR';
import { RedirectExternalPageSelectedBank } from '@modules/redirect-to-external-bank/redirect-to-external-bank.component';
import { Buffer } from 'buffer';
import { ConnectionFormItemDto } from './pages/sca-waiting/services/dtos/scaDtos';

@Component({
  selector: 'app-process',
  templateUrl: './process.component.html',
})
export class ProcessComponent implements OnInit {
  private initVM: InitVM;
  connectionErrorMesage!: string;
  initEnd = false;

  constructor(
    private processEventService: ProcessEventService,
    private storeService: StoreService,
    private caseService: CaseService,
    private translate: TranslateService,
    @Inject(browserBroadcastToken) private broadcastService: BrowserBroadcast,
    @Inject(loggerToken) private logger: Logger,
    private router: Router,
    private signalR: SignalR,
    @Inject(isSignalREnabledToken) private isSignalREnabled: boolean,
    @Inject(isTrackingEnabledToken) private isTrackingEnabled: boolean,
    private tracker: TrackerService
  ) {
    this.initVM = new InitVM(storeService, logger);
    this.handleWebauthKO();
    this.handleWebauthHasUserStoppedWaitingCheck();
  }

  async ngOnInit(): Promise<void> {
    this.processEventService.subscribe(this.handleEvent.bind(this));

    await this.initializeServices();

    this.handleFirstTimeProcessRouteReached();
    this.handleProcessPageRefresh();

    this.initEnd = true;
  }

  handleEvent(event: AcceptedProcessEvent | ParentProcessEvent) {
    switch (event.type) {
      case ProcessEventType.CONSENT:
        this.handleConsent(event);
        break;
      case ProcessEventType.BANK_CHOICE:
        this.handleBankChoice(event);
        break;
      case ProcessEventType.TRIALS_EXHAUSTED:
        this.handleTrialsExhausted(event);
        break;
      case ProcessEventType.GO_BACK_TO_BANK_CHOICE:
        this.handleBackToBankChoice(event);
        break;
      case ProcessEventType.ACCOUNTS_FOUND:
        this.handleAccountsFoundEvent(event);
        break;
      case ProcessEventType.SCA:
        this.handleScaEvent(event);
        break;
      case ProcessEventType.SCA_FAILED:
        this.handleScaFailed(event);
        break;
      case ProcessEventType.CONTINUE_AFTER_ERROR:
        this.handleContinueAfterError(event);
        break;
      case ProcessEventType.ABANDON:
        this.handleAbandon(event);
        break;
      case ProcessEventType.SCA_GO_BACK:
        this.handleScaGoBack(event);
        break;
      case ProcessEventType.SELECTED_ACCOUNTS:
        this.handleSelectedAccounts(event);
        break;
      case ProcessEventType.CONTINUE:
        this.handleContinueEvent(event);
        break;
      case ProcessEventType.INTERNAL_ERROR:
        this.handleInternalErrorEvent(event);
        break;
      case ProcessEventType.WAIT_ON_EXTERNAL_BANK_LOGIN:
        this.handleWaitOnExternalBankConnectEvent();
        break;
      case ProcessEventType.ASK_TO_RETRY_WEBAUTH:
        this.handleAskToRetryWebauth();
        break;
      case ProcessEventType.RETRY_WEBAUTH:
        this.handleRetryWebauth();
        break;
      case ProcessEventType.LOGIN_WEBAUTH_WITH_FORM:
        this.handleLoginWebauthWithForm(event);
        break;
      case ProcessEventType.WINDOW_RESIZE_EVENT:
        break;
      default:
        this.neverCase(event);
    }
  }

  private neverCase(_input: never): never {
    throw new Error('should never go here');
  }

  handleConsent(_consentEvent: ConsentEvent) {
    this.navigateToProcessPage({ page: 'BANK_CHOICE', params: null });
  }

  handleBankChoice(bankChoiceEvent: BankChoiceEvent) {
    this.storeService.setVariable('SELECTED_BANK', bankChoiceEvent.data);

    const isWebauth = this.storeService.getVariableOrThrow('SELECTED_BANK').authMechanism == 'webauth';
    const hasSigninForm = this.storeService.getVariableOrThrow('SELECTED_BANK').form.length > 0;

    if (isWebauth && hasSigninForm == false) {
      this.handleExternalBankLoginFlow(null);
    } else {
      this.navigateToProcessPage({ page: 'LOGIN', params: null }); // TODO wording : LOGIN_WITH_FORM - BankConnectionForm ?
    }
  }

  handleLoginWebauthWithForm(event: LoginWebauthWithForm) {
    const form = { value: event.form };
    this.handleExternalBankLoginFlow(form);
  }

  handleBackToBankChoice(_event: GoBackToBankChoiceEvent) {
    this.navigateToProcessPage({ page: 'BANK_CHOICE', params: null });
  }

  handleScaGoBack(_event: ScaGoBackEvent) {
    this.navigateToProcessPage({ page: 'LOGIN', params: null });
  }

  handleScaEvent(_event: ScaEvent) {
    this.navigateToProcessPage({ page: 'SCA_WAITING_SCREEN', params: null });
  }

  handleScaFailed(_event: ScaFailedEvent) {
    this.connectionErrorMesage = this.translate.instant('connection_error_sca_failed');
    this.navigateToProcessPage({ page: 'LOGIN', params: null });
  }

  handleTrialsExhausted(_trialsExhaustedEvent: TrialsExhaustedEvent) {
    this.navigateToErrorRoute();
  }

  handleAccountsFoundEvent(event: AccountsFoundEvent) {
    this.storeService.setVariable('FOUND_ACCOUNTS', event.data);
    const nbAccounts = event.data.length;

    if (!nbAccounts) {
      this.navigateToProcessPage({ page: 'NONE_ACCOUNTS', params: null });
      return;
    }

    this.navigateToProcessPage({ page: 'SELECT_ACCOUNTS', params: null });
  }

  handleSelectedAccounts(event: SelectAccountsEvent) {
    this.navigateToProcessPage({
      page: 'SELECT_ACCOUNTS_ANSWER',
      params: {
        selectedAccount: event.data,
        status: event.status,
        taggingEventOrigin: event.taggingEventOrigin,
      },
    });
  }

  handleAbandon(_event: AbandonEvent) {
    this.caseService
      .abortCase(
        this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN'),
        this.storeService.getVariableOrThrow('USER_ID')
      )
      .subscribe((_) => {
        this.storeService.reset();
        window.parent.postMessage('ABORTED', '*');
        this.logger.logEvent('END_OF_SEQUENCE', { success: false, hasErrors: false });
      });
  }

  handleContinueEvent(_event: ContinueEvent) {
    this.storeService.reset();
    window.parent.postMessage('SUCCESS', '*');
    this.logger.logEvent('END_OF_SEQUENCE', { success: true, hasErrors: false });
  }

  handleContinueAfterError(_event: ContinueAfterErrorEvent) {
    this.storeService.reset();
    window.parent.postMessage('ERROR', '*');
    this.logger.logEvent('END_OF_SEQUENCE', { success: false, hasErrors: true });
  }

  handleInternalErrorEvent(event: InternalErrorEvent) {
    console.log('handleInternalErrorEvent : ', event);

    this.caseService
      .failCase(
        this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN'),
        this.storeService.getVariableOrThrow('USER_ID')
      )
      .subscribe((_) => {
        this.navigateToErrorRoute();
      });
  }

  handleWaitOnExternalBankConnectEvent() {
    this.navigateToProcessPage({ page: 'WAIT_ON_EXTERNAL_BANK_LOGIN', params: null });
  }

  handleAskToRetryWebauth() {
    this.navigateToProcessPage({
      page: 'BANK_CONNECTION_FAILURE_RETRY',
      params: { origin: 'RETRY_WEBAUTH' },
    });
  }

  handleRetryWebauth() {
    this.navigateToProcessPage({ page: 'BANK_CHOICE', params: null });
  }

  handleWebauthKO() {
    this.broadcastService.getMessageFromLabel('CONNECTION_EXTERNAL_BANK_KO').subscribe(() => {
      const isWebauthCancelled =
        this.storeService.getVariableOrThrow('PROCESS_PAGE') === 'WAIT_ON_EXTERNAL_BANK_LOGIN' ? false : true;

      if (!isWebauthCancelled) {
        this.processEventService.push(ProcessEventFactory.SpawnAskToRetryWebauth());
      }
    });
  }

  handleWebauthHasUserStoppedWaitingCheck() {
    this.broadcastService.getMessageFromLabel('WEBAUTH_POPUP_REACHED').subscribe(() => {
      const hasUserStoppedWaiting =
        this.storeService.getVariableOrThrow('PROCESS_PAGE') === 'WAIT_ON_EXTERNAL_BANK_LOGIN' ? false : true;

      this.broadcastService.publish({
        label: 'WEBAUTH_POPUP_ANSWER_HAS_USER_STOPPED_WAITING',
        data: { hasUserStoppedWaiting },
      });
    });
  }

  navigateToProcessPage<T extends ProcessPage>(processPageNavigation: ProcessPageNavigation<T>) {
    console.log(`Render process page : ${processPageNavigation.page}`);
    this.storeService.setVariable('PROCESS_PAGE', processPageNavigation.page);
    this.storeService.setVariable('PROCESS_PAGE_PARAMS', processPageNavigation.params);

    const processPageHistory = this.storeService.getVariableOrThrow('PROCESS_PAGE_HISTORY');
    processPageHistory.push(processPageNavigation.page);
    this.storeService.setVariable('PROCESS_PAGE_HISTORY', processPageHistory);

    if (processPageNavigation.page !== 'CONSENT') {
      history.pushState(processPageNavigation.page, '', '/process');
    }
  }

  navigateToErrorRoute() {
    this.router.navigate(['error'], { state: { isWrongUrl: false } });
  }

  handleExternalBankLoginFlow(form: Option<ConnectionFormItemDto[]>) {
    /* 
      In order to open the popup without asking the user for authorization,
      we need to open the popup after an explicit user interaction (click).
      There should be no delay and no asynchronous operation, otherwise,
      it's not considered as an explicit user interaction.

      Thus : 
        1. Open new tab
        2. Add content to new tab to inform user we wait external bank url
        3. Redirect to webauth url
    */
    const parsedUrl = new URL(window.location.href);
    const baseUrl = `${parsedUrl.protocol}//${parsedUrl.hostname}${parsedUrl.port ? `:${parsedUrl.port}` : ''}`;

    const selectedBank = this.storeService.getVariableOrThrow('SELECTED_BANK');
    const redirectSelectedBank: RedirectExternalPageSelectedBank = {
      logo: selectedBank.logo ? { value: selectedBank.logo } : null,
      name: selectedBank.name,
      uid: selectedBank.uid,
    };
    const redirectSelectedBankStringified = Buffer.from(JSON.stringify(redirectSelectedBank)).toString(
      'base64'
    );
    const userToken = this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN');
    const tagging = this.storeService.getVariable('TAGGING_INIT_DATA_IN_BASE64');
    const formStringified = Buffer.from(JSON.stringify(form)).toString('base64');

    let queryParams = `?webauthForm=${formStringified}&selectedBank=${redirectSelectedBankStringified}&userToken=${userToken}`;

    if (tagging != null) {
      queryParams += `&tagging=${tagging}`;
    }

    const webauthTab = window.open(`${baseUrl}/redirect-to-external-bank${queryParams}`);

    this.navigateToProcessPage({
      page: 'WAIT_ON_EXTERNAL_BANK_LOGIN',
      params: null,
    });

    this.broadcastService.getMessageFromLabel('CLOSE_WEBAUTH_POPUP').subscribe(() => {
      webauthTab?.close();
    });

    this.broadcastService.getMessageFromLabel('REDIRECT_TO_EXTERNAL_BANK_LOGIN_ERROR').subscribe(() => {
      this.navigateToProcessPage({
        page: 'BANK_CONNECTION_FAILURE_RETRY',
        params: { origin: 'REDIRECT_TO_EXTERNAL_BANK' },
      });
    });
  }

  private async initializeServices() {
    await this.initVM.initializeSignalR(this.isSignalREnabled, this.signalR);
    await this.initVM.initializeTaggingTracker(this.isTrackingEnabled, this.tracker);
  }

  /*
    When page is refreshed, Angular pushes a state which looks like : {navigationId: 1}
    In order to avoid back and forward inconsistency, we need to replace the state pushed
    by the page before refresh
  */
  private handleProcessPageRefresh() {
    const processPageBeforeRefresh = this.storeService.getVariableOrThrow('PROCESS_PAGE');
    history.replaceState(processPageBeforeRefresh, '', '/process');
  }

  private handleFirstTimeProcessRouteReached() {
    if (this.storeService.getVariable('PROCESS_PAGE') === null) {
      /*
        When navigating to a route, Angular pushes a state which looks like : {navigationId: 1}
        In order to avoid an unwanted state, we replace it directly by the CONSENT process page
        the first time the route is reached
      */
      history.replaceState('CONSENT', '', '/process');

      this.storeService.setVariable('PROCESS_PAGE_HISTORY', []);
      this.navigateToProcessPage({ page: 'CONSENT', params: null });
    }
  }

  get processPage(): ProcessPage | null {
    return this.storeService.getVariable('PROCESS_PAGE');
  }
}
